import { useCallback, useEffect, useRef } from "react";
import { usePathname } from "next/navigation";
import { useMediaQuery } from "react-responsive";

interface UseMobileHistoryStateOptions {
  isOpen: boolean;
  onClose: () => void;
  modalId: string;
  mobileBreakpoint?: string;
  disabled?: boolean;
}

interface MobileModalHistoryState {
  modal?: string;
}

/**
 * Hook to handle browser history for mobile modals/slideins.
 * When a modal opens on mobile, it pushes a new history state.
 * When the user navigates back (swipe or back button), it closes the modal
 * instead of navigating to a previous page.
 */
export const useMobileHistoryState = ({
  isOpen,
  onClose,
  modalId,
  mobileBreakpoint = "(max-width: 1250px)",
  disabled = false,
}: UseMobileHistoryStateOptions) => {
  const isMobile = useMediaQuery({ query: mobileBreakpoint });
  const pathname = usePathname();
  const historyPushedRef = useRef(false);
  const closingRef = useRef(false);
  const pendingBackRef = useRef<number | null>(null);
  const isOpenRef = useRef(isOpen);
  const onCloseRef = useRef(onClose);
  const pathnameRef = useRef(pathname);
  // Pathname captured at the moment the synthetic history entry was pushed —
  // compared against the live URL right before firing history.back() (see
  // below), not React's own pathname state, since that can lag behind an
  // in-flight navigation by the time the timeout fires.
  const openedPathnameRef = useRef(pathname);

  const clearPendingBack = useCallback(() => {
    if (pendingBackRef.current !== null) {
      window.clearTimeout(pendingBackRef.current);
      pendingBackRef.current = null;
    }
  }, []);

  // Keep refs up to date
  useEffect(() => {
    if (disabled) return;
    isOpenRef.current = isOpen;
    onCloseRef.current = onClose;
  }, [isOpen, onClose, disabled]);

  // Track route changes - when route changes, just reset the history state without going back
  useEffect(() => {
    if (disabled) return;
    if (pathnameRef.current !== pathname) {
      // Route changed - the navigation already happened, don't call history.back()
      clearPendingBack();
      historyPushedRef.current = false;
      pathnameRef.current = pathname;
    }
  }, [clearPendingBack, pathname, disabled]);

  useEffect(() => {
    if (disabled || !isMobile) {
      clearPendingBack();
      historyPushedRef.current = false;
      return;
    }

    // When modal opens, push a new history state
    if (isOpen && !historyPushedRef.current) {
      clearPendingBack();
      if ((window.history.state as MobileModalHistoryState | null)?.modal !== modalId) {
        window.history.pushState({ modal: modalId }, "");
      }
      historyPushedRef.current = true;
      // Read via the ref (kept current by the route-tracking effect above,
      // which runs first within the same commit) rather than the raw
      // `pathname` closure value, so this effect doesn't need `pathname` in
      // its own dependency array purely to re-run on every route change.
      openedPathnameRef.current = pathnameRef.current;
    }

    // Wait a tick so a newly opened top layer can claim the current history entry.
    // A layer that closes underneath another modal must not unwind that modal's entry.
    if (!isOpen && historyPushedRef.current && !closingRef.current) {
      clearPendingBack();
      pendingBackRef.current = window.setTimeout(() => {
        pendingBackRef.current = null;

        const currentModalId = (window.history.state as MobileModalHistoryState | null)?.modal;
        if (currentModalId !== modalId) {
          historyPushedRef.current = false;
          return;
        }

        // The modal can also close as a side effect of navigating to a real
        // page from inside it (a Link or router.push, not just a close
        // button/backdrop click). The currentModalId check above already
        // catches this once Next's own pushState has committed the new
        // route, but on a slow-loading destination (no prefetch, heavier
        // page) that commit can still be pending when this timeout fires —
        // window.history.state momentarily still matches this modal even
        // though the browser is mid-navigation. Comparing the live pathname
        // against the one captured when this modal opened catches that
        // window too; calling history.back() in it would silently undo the
        // navigation, bouncing the user back to the page the modal was
        // opened on instead of the one they just navigated to.
        if (window.location.pathname !== openedPathnameRef.current) {
          historyPushedRef.current = false;
          return;
        }

        historyPushedRef.current = false;
        window.history.back();
      }, 0);
    }
  }, [clearPendingBack, isOpen, isMobile, modalId, disabled]);

  useEffect(() => {
    return () => {
      clearPendingBack();
    };
  }, [clearPendingBack]);

  useEffect(() => {
    if (disabled || !isMobile) return;

    const handlePopState = (event: PopStateEvent) => {
      if (!isOpenRef.current || !historyPushedRef.current) return;

      const revealedModalId = (event.state as MobileModalHistoryState | null)?.modal;

      // This layer is the state revealed by Back, so it remains open. Only a layer
      // above the revealed state should close.
      if (revealedModalId === modalId) return;

      closingRef.current = true;
      historyPushedRef.current = false;
      onCloseRef.current();

      // Reset the closing flag after a short delay
      setTimeout(() => {
        closingRef.current = false;
      }, 100);
    };

    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [isMobile, disabled, modalId]);
};
